<script setup lang="ts">
import { computed, ref } from "vue";

const props = defineProps<{
  goldTola: number;
  silverTola: number;
  goldPerTola: number;
  silverPerTola: number;
  goldValue: number;
  silverValue: number;
}>();

const emit = defineEmits<{
  (e: "update:goldTola", value: number): void;
  (e: "update:silverTola", value: number): void;
}>();

const isOpen = ref(false);

// IDs for inputs so labels are programmatically associated
const goldId = `zakat-gold-${Math.random().toString(36).slice(2, 9)}`;
const silverId = `zakat-silver-${Math.random().toString(36).slice(2, 9)}`;

const formattedGoldRate = computed(() =>
  new Intl.NumberFormat("en-PK").format(props.goldPerTola),
);
const formattedSilverRate = computed(() =>
  new Intl.NumberFormat("en-PK").format(props.silverPerTola),
);
const formattedGoldValue = computed(() =>
  new Intl.NumberFormat("en-PK").format(props.goldValue),
);
const formattedSilverValue = computed(() =>
  new Intl.NumberFormat("en-PK").format(props.silverValue),
);

function normalizeTola(raw: string): number {
  const parsed = Number.parseFloat(raw);
  if (!Number.isFinite(parsed) || parsed < 0) return 0;
  return Math.round(parsed * 10) / 10;
}

function onGoldInput(event: Event) {
  const target = event.target as HTMLInputElement;
  emit("update:goldTola", normalizeTola(target.value));
}

function onSilverInput(event: Event) {
  const target = event.target as HTMLInputElement;
  emit("update:silverTola", normalizeTola(target.value));
}
</script>

<template>
  <section
    class="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
  >
    <button
      class="w-full flex items-center justify-between px-4 py-3 text-left text-sm font-medium text-gray-800 dark:text-gray-100"
      @click="isOpen = !isOpen"
    >
      <span>{{
        isOpen ? "Hide Gold & Silver" : "Add Gold & Silver (Optional)"
      }}</span>
      <span
        class="text-sm text-gray-500 dark:text-gray-400 transition-transform duration-200"
        :class="isOpen ? 'rotate-180' : ''"
      >
        v
      </span>
    </button>

    <transition name="expand">
      <div
        v-if="isOpen"
        class="px-4 pb-4 space-y-4 border-t border-gray-200 dark:border-gray-700"
      >
        <p class="text-xs text-gray-500 dark:text-gray-400 pt-3">
          Yahan enter kiya gaya gold aur silver automatically total mein add ho
          jaye ga.
        </p>

        <div class="space-y-1">
          <label :for="goldId" class="text-sm text-gray-700 dark:text-gray-300"
            >Gold (tola)</label
          >
          <input
            :id="goldId"
            :value="goldTola"
            type="number"
            min="0"
            step="0.1"
            inputmode="decimal"
            class="w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 outline-none"
            @input="onGoldInput"
          />
          <p class="text-xs text-gray-500 dark:text-gray-400">
            {{ goldTola }} tola x Rs {{ formattedGoldRate }} = Rs
            {{ formattedGoldValue }}
          </p>
        </div>

        <div class="space-y-1">
          <label
            :for="silverId"
            class="text-sm text-gray-700 dark:text-gray-300"
            >Silver (tola)</label
          >
          <input
            :id="silverId"
            :value="silverTola"
            type="number"
            min="0"
            step="0.1"
            inputmode="decimal"
            class="w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 outline-none"
            @input="onSilverInput"
          />
          <p class="text-xs text-gray-500 dark:text-gray-400">
            {{ silverTola }} tola x Rs {{ formattedSilverRate }} = Rs
            {{ formattedSilverValue }}
          </p>
        </div>
      </div>
    </transition>
  </section>
</template>

<style scoped>
.expand-enter-active,
.expand-leave-active {
  transition:
    max-height 0.2s ease,
    opacity 0.2s ease;
  max-height: 600px;
  overflow: hidden;
}

.expand-enter-from,
.expand-leave-to {
  max-height: 0;
  opacity: 0;
}
</style>
